import React, { FC, useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  fetchTaskThresholdSetting,
  updateTaskThresholdSetting,
} from '../../../../states/containers/task/taskSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RequestPatchTaskThresholdSetting, TaskThresholdMember } from '../../../../states/types/task'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "制限無し",
    value: 1,
  },
  {
    label: "30秒",
    value: 30,
  },
  ...Array(10).fill(null).map((_v, i) => (
    {
      label: `${i + 1}分`,
      value: 60 * (i + 1),
    }
  )),
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: null,
  },
  {
    label: "制限無しに設定する",
    value: 1,
  },
  {
    label: "30秒に設定する",
    value: 30,
  },
  ...Array(10).fill(null).map((_v, i) => (
    {
      label: `${i + 1}分に設定する`,
      value: 60 * (i + 1),
    }
  )),
]

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [thresholdTime, setThresholdTime] = useState<number>(1)

  const [originMemberList, setOriginMemberList] = useState<TaskThresholdMember[]>([])
  const [commonValue, setCommonValue] = useState<number>(1)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<TaskThresholdMember[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const changeThreshold = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchTaskThresholdSetting = { change_common_setting: data.commonFlg }
    const task_threshold = data.type
    if (data.commonFlg) {
      param.task_threshold = Number(task_threshold)
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = task_threshold === "common"
      if (data.type !== "common") {
        param.task_threshold = Number(task_threshold)
      }
    }
    dispatch(updateTaskThresholdSetting(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(Number(task_threshold))
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? {
                ...v,
                use_common_setting: param.use_common_setting || false,
                task_threshold: task_threshold === "common" ? null : Number(task_threshold),
              }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskThresholdSetting(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.task_threshold)
        setThresholdTime(result.common.task_threshold)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="task_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_THRESHOLD_TIME_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 業務仕分けボードに表示する作業データの最小時間`
            ) : (
              `Qasee - 工数管理 - 設定 - その他 - 業務仕分けボードに表示する作業データの最小時間`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_THRESHOLD_TIME_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
              &nbsp;&gt;&nbsp;
              設定
              &nbsp;&gt;&nbsp;
              その他
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        業務仕分けボードに表示する作業データの最小時間
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          業務仕分けボードに表示する作業データの最小時間
        </h1>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>業務仕分けボードに表示する作業データの最小時間を分単位で設定します。</p>
        <p style={{ marginBottom: "4px" }}>最小時間が大きいほど作業データの表示数は減り仕分けしやすくなりますが、最小時間以下の作業データを仕分けることができなくなり精度が減少する可能性があります。</p>
        <p style={{ marginBottom: "4px" }}>
          詳しい設定方法は
          <a href="https://qasee.zendesk.com/hc/ja/articles/43486827998489" target="_blank" rel="noreferrer">ヘルプページ</a>
          をご確認いただくか、カスタマーサクセス窓口までご連絡ください。
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header" style={{ display: "block" }}>
                      <div className="title" style={{ marginBottom: "16px" }}>
                        会社全体の設定
                      </div>
                    </div>
                    <div
                      className="block-detail"
                      style={{
                        border: "solid 1px #CCC",
                        borderRadius: "4px",
                        padding: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          marginBottom: "16px",
                        }}
                      >
                        業務仕分けボードに表示する作業データの最小時間を
                        <select
                          onChange={(e) => setThresholdTime(Number(e.target.value))}
                          value={thresholdTime === null ? "common" : thresholdTime.toString()}
                          defaultValue={thresholdTime === null ? "common" : thresholdTime.toString()}
                          style={{
                            border: "solid 1px #999",
                            backgroundColor: "#FFF",
                            borderRadius: "3px",
                            padding: "6px 8px",
                            appearance: "listbox",
                            WebkitAppearance: "listbox",
                          }}
                        >
                          {
                            STATUS_LIST.map((v) => (
                              <option value={v.value} key={`option_${v.value}`}>
                                {v.label}
                              </option>
                            ))
                          }
                        </select>
                        に設定します (初期設定: 3分)
                      </div>
                      <button
                        type="button"
                        className="button"
                        onClick={() => changeThreshold({ commonFlg: true, type: thresholdTime === null ? "common" : thresholdTime.toString() })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.task_threshold }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="業務仕分けボードに表示する作業データの最小時間"
              type="number"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: v.task_threshold?.toString() || "common" }))}
              selectList={SELECT_LIST.map((v) => (v.value ? ({ ...v, value: v.value.toString() }) : ({ ...v, value: "common" })))}
              label="業務仕分けボードに表示する作業データの最小時間"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeThreshold(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
